<!--
  PROPOSTA DI UN PACCHETTO VALET — prezzo dal listino.

  Lo usano la creazione cliente e la scheda cliente. Il prezzo si calcola:
    1. distanza su strada dal punto di partenza → sede scelta (preventivo
       valet). Il punto di partenza è la casa del cliente, oppure un altro
       indirizzo scritto dal venditore; la sede si vede sempre, anche se è
       una sola, perché è l'altro capo della distanza che fa la fascia;
    2. fascia del listino → prezzo Simba di un pickup all'anno per 1, 2, 3
       pickup all'anno;
    3. pickup all'anno e durata in mesi scelti dal venditore → totale e rata.

  A schermo SOLO il prezzo con le tariffe del partner (quello che paga il
  cliente), come nel preventivo valet. Sul pacchetto si salvano il prezzo
  Simba e quello del venditore, più la fascia: il backend li ricalcola dalla
  fascia. Importi senza IVA.

  Emette `change` con `{ problema, dati }`: `dati` è pronto per
  `proponiPacchetto` quando `problema` è vuoto.
-->
<template>
  <div class="pv">
    <!-- ---- percorso su cui si calcolano i km del valet ---- -->
    <div class="pv__route">
      <div class="pv__end">
        <span class="campo-label">Da</span>
        <div
          class="pv__seg"
          role="radiogroup"
          aria-label="Punto di partenza per i km del valet"
        >
          <button
            type="button"
            role="radio"
            :aria-checked="modoPartenza === 'casa'"
            :class="{ 'is-on': modoPartenza === 'casa' }"
            @click="modoPartenza = 'casa'"
          >
            Casa del cliente
          </button>
          <button
            type="button"
            role="radio"
            :aria-checked="modoPartenza === 'altro'"
            :class="{ 'is-on': modoPartenza === 'altro' }"
            @click="modoPartenza = 'altro'"
          >
            Un altro indirizzo
          </button>
        </div>

        <p
          v-if="modoPartenza === 'casa'"
          class="pv__addr"
          :class="{ 'pv__addr--empty': !casaPronta }"
        >
          <span
            class="pv__pin pv__pin--from"
            aria-hidden="true"
          />
          {{ casaPronta ? testoIndirizzo(casa) : "Indirizzo del cliente non ancora completo" }}
        </p>
        <div
          v-else
          class="pv__grid pv__grid--addr"
        >
          <div class="pv__f pv__f--2">
            <label
              class="campo-label"
              :for="`${uid}-via`"
            >Via / Piazza</label>
            <el-input
              :id="`${uid}-via`"
              v-model="altro.address"
            />
          </div>
          <div class="pv__f">
            <label
              class="campo-label"
              :for="`${uid}-civico`"
            >Civico</label>
            <el-input
              :id="`${uid}-civico`"
              v-model="altro.street_number"
            />
          </div>
          <div class="pv__f">
            <label
              class="campo-label"
              :for="`${uid}-cap`"
            >CAP</label>
            <el-input
              :id="`${uid}-cap`"
              v-model="altro.cap"
              maxlength="5"
            />
          </div>
          <div class="pv__f">
            <label
              class="campo-label"
              :for="`${uid}-citta`"
            >Città</label>
            <el-input
              :id="`${uid}-citta`"
              v-model="altro.city"
            />
          </div>
          <div class="pv__f">
            <label
              class="campo-label"
              :for="`${uid}-prov`"
            >Provincia</label>
            <el-input
              :id="`${uid}-prov`"
              v-model="altro.province"
              maxlength="2"
              placeholder="es. BG"
            />
          </div>
        </div>
      </div>

      <div class="pv__end">
        <label
          class="campo-label"
          :for="`${uid}-sede`"
        >A — sede del partner</label>
        <p
          v-if="!sedi.length"
          class="pv__addr pv__addr--empty"
        >
          Il partner non ha sedi.
        </p>
        <!-- Anche con una sola sede: si vede dove arriva la distanza. -->
        <el-select
          v-else
          :id="`${uid}-sede`"
          v-model="sedeId"
          placeholder="Scegli la sede"
          class="pv__sede"
        >
          <el-option
            v-for="s in sedi"
            :key="s.id"
            :label="etichettaSede(s)"
            :value="s.id"
          />
        </el-select>
      </div>

      <p
        class="pv__where"
        aria-live="polite"
      >
        <template v-if="stato === 'calcolo'">
          Calcolo la distanza dalla sede…
        </template>
        <template v-else-if="preventivo && preventivo.band">
          {{ preventivo.km.toLocaleString('it-IT') }} km su strada {{ modoPartenza === 'casa' ? 'da casa del cliente' : "dall'indirizzo scelto" }} alla sede · fascia {{ fascia(preventivo.band) }}
          <template v-if="preventivo.scope === 'global'">
            · listino generale Simba
          </template>
        </template>
      </p>
    </div>
    <br>

    <div class="pv__grid">
      <!-- Il prezzo è per PICKUP, a scaglioni: il listino ha il prezzo di uno,
           di due e di tre o più. Sopra i tre vale il prezzo della terza riga. -->
      <div class="pv__f">
        <label
          class="campo-label"
          :for="`${uid}-pickup`"
        >Numero di pickup</label>
        <el-input-number
          :id="`${uid}-pickup`"
          v-model="pickup"
          :min="1"
          :max="99"
          :step="1"
          step-strictly
          controls-position="right"
        />
      </div>
      <div class="pv__f">
        <label
          class="campo-label"
          :for="`${uid}-mesi`"
        >Durata</label>
        <el-select
          :id="`${uid}-mesi`"
          v-model="mesi"
          class="pv__mesi"
        >
          <el-option
            v-for="m in DURATE"
            :key="m"
            :label="`${m} mesi (${m / 12} ${m === 12 ? 'anno' : 'anni'})`"
            :value="m"
          />
        </el-select>
      </div>
    </div>

   

    <div
      v-if="numeri"
      class="fx-stats"
    >
      <span class="fx-stat">
        <span class="fx-stat__l">Valet compresi</span>
        <span class="fx-stat__v">{{ numeri.valetTotali }}</span>
      </span>
      <!-- Solo i prezzi con le tariffe del partner, come nel preventivo valet:
           è quello che paga il cliente, e chi propone il pacchetto ha il cliente
           davanti. Il prezzo Simba non si mostra, ma si salva sul pacchetto
           insieme a questo (`dati.totaleSimba`, `dati.totaleVenditore`). -->
      <span class="fx-stat fx-stat--euro">
        <span class="fx-stat__l">Al cliente, a pickup</span>
        <span class="fx-stat__v">{{ numeri.unitVenditore === null ? '—' : `${euro(numeri.unitVenditore)} + IVA` }}</span>
      </span>
      <span class="fx-stat">
        <span class="fx-stat__l">Al cliente, in tutto</span>
        <span class="fx-stat__v">{{ numeri.totaleVenditore === null ? '—' : `${euro(numeri.totaleVenditore)} + IVA` }}</span>
      </span>
      <!-- Rateizzazione sulla durata scelta: il prezzo non dipende dai mesi,
           ma «quanto viene al mese» è la prima domanda del cliente. -->
      <span class="fx-stat">
        <span class="fx-stat__l">Al cliente, al mese su {{ numeri.mesi }}</span>
        <span class="fx-stat__v">{{ numeri.mensileVenditore === null ? '—' : `${euro(numeri.mensileVenditore)} + IVA` }}</span>
      </span>
      <span class="fx-stat">
        <span class="fx-stat__l">Scade il</span>
        <span class="fx-stat__v">{{ numeri.scadenza.format('DD/MM/YYYY') }}</span>
      </span>
    </div>

    <p
      v-if="numeri && numeri.unitVenditore === null && !problema"
      class="pv__where"
    >
      Il prezzo al cliente per questa fascia non è impostato: lo imposta l'amministratore in Tariffe.
    </p>

    <p
      v-if="problema"
      class="pv__problem"
    >
      {{ problema }}
      <el-button
        v-if="stato === 'errore'"
        size="small"
        text
        @click="calcola"
      >
        Riprova
      </el-button>
    </p>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { numeriPacchetto, preventivoValet } from '../services/nuovoClienteAPI';

const pulito = (v) => String(v ?? '').trim();

// La durata si sceglie di anno in anno, fino a otto: sono contratti annuali,
// e un campo libero lasciava scrivere durate che nessuno vende (7 mesi, 13…).
const DURATE = [12, 24, 36, 48, 60, 72, 84, 96];
const euroFmt = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
let sequenza = 0;

export default {
  name: 'PropostaValet',

  props: {
    // Indirizzo del cliente: { address, street_number, cap, city, province }.
    indirizzo: { type: Object, required: true },
    // Sedi del partner: si mostrano sempre, la prima è già scelta.
    sedi: { type: Array, default: () => [] },
  },

  emits: ['change'],

  setup(props, { emit }) {
    sequenza += 1;
    const uid = `pv-${sequenza}`;
    const sedeId = ref(props.sedi[0]?.id || '');
    const pickup = ref(1);
    const mesi = ref(12);
    const stato = ref('idle'); // idle | calcolo | ok | errore
    const preventivo = ref(null);
    const erroreServer = ref('');

    watch(() => props.sedi, (elenco) => {
      if (!elenco.some((s) => s.id === sedeId.value)) sedeId.value = elenco[0]?.id || '';
    });

    // Punto di partenza dei km: la casa del cliente (default) o un altro
    // indirizzo. L'altro indirizzo resta scritto se si torna a «casa» e poi
    // di nuovo ad «altro», così non si perde quello che si era digitato.
    const modoPartenza = ref('casa');
    const altro = reactive({ address: '', street_number: '', cap: '', city: '', province: '' });

    const normalizza = (a) => ({
      address: pulito(a?.address),
      street_number: pulito(a?.street_number),
      cap: pulito(a?.cap),
      city: pulito(a?.city),
      province: pulito(a?.province).toUpperCase(),
    });
    const casa = computed(() => normalizza(props.indirizzo));
    const casaPronta = computed(() => !!(casa.value.address && casa.value.city));
    const origine = computed(() => (modoPartenza.value === 'casa' ? casa.value : normalizza(altro)));
    const indirizzoPronto = computed(() => !!(origine.value.address && origine.value.city));
    const testoIndirizzo = (a) => [
      [a.address, a.street_number].filter(Boolean).join(' '),
      [a.cap, a.city].filter(Boolean).join(' '),
      a.province ? `(${a.province})` : '',
    ].filter(Boolean).join(', ').replace(', (', ' (');

    // La richiesta di ADESSO: una risposta arrivata tardi per un indirizzo già
    // cambiato non deve sovrascrivere quella giusta.
    let richiesta = 0;
    const calcola = async () => {
      if (!indirizzoPronto.value || !sedeId.value) {
        preventivo.value = null;
        stato.value = 'idle';
        return;
      }
      const mia = ++richiesta;
      stato.value = 'calcolo';
      try {
        const r = await preventivoValet({ origin: origine.value, headquarterId: sedeId.value });
        if (mia !== richiesta) return;
        preventivo.value = r;
        stato.value = 'ok';
      } catch (e) {
        if (mia !== richiesta) return;
        preventivo.value = null;
        erroreServer.value = e?.response?.data?.message || '';
        stato.value = 'errore';
      }
    };

    let timer = null;
    watch([origine, sedeId], () => {
      clearTimeout(timer);
      timer = setTimeout(calcola, 500);
    }, { deep: true, immediate: true });
    onBeforeUnmount(() => clearTimeout(timer));

    // La riga di listino da usare: uno, due, o «tre o più».
    const fasciaPrezzo = computed(() => Math.min(3, Math.max(1, Number(pickup.value) || 1)));
    const opzione = computed(
      () => preventivo.value?.options?.find((o) => o.pickups_per_year === fasciaPrezzo.value) || null
    );
    const numeri = computed(
      () => (opzione.value ? numeriPacchetto(opzione.value, mesi.value, pickup.value) : null)
    );

    const problema = computed(() => {
      if (!indirizzoPronto.value) {
        return modoPartenza.value === 'casa'
          ? "Completa l'indirizzo del cliente, oppure scegli un altro indirizzo: il prezzo dipende dalla distanza dalla sede."
          : "Scrivi almeno via e città dell'indirizzo di partenza: il prezzo dipende dalla distanza dalla sede.";
      }
      if (!sedeId.value) return 'Il partner non ha sedi: il prezzo non si può calcolare.';
      if (stato.value === 'calcolo' || stato.value === 'idle') return 'Calcolo del prezzo in corso…';
      if (stato.value === 'errore') {
        return `Non riesco a calcolare la distanza${erroreServer.value ? ` (${erroreServer.value})` : ''}: controlla l'indirizzo.`;
      }
      if (!preventivo.value?.band) return 'Per questa distanza il listino valet non ha una fascia: il pacchetto non si può proporre.';
      if (!numeri.value || numeri.value.totaleSimba === null) {
        return `Il listino non ha il prezzo per ${fasciaPrezzo.value === 3 ? '3 o più' : fasciaPrezzo.value} pickup in questa fascia. Parlane con Simba.`;
      }
      if (!numeri.value.valetTotali) return 'Con questa durata il pacchetto non comprende nessun valet: aumenta i mesi.';
      // Stessa regola del backend (`MinuteService.pricesFromBand`), che
      // altrimenti rifiuta la proposta con un 400 solo dopo il clic.
      if (numeri.value.totaleVenditore !== null && numeri.value.totaleVenditore < numeri.value.totaleSimba) {
        return "Nelle Tariffe il prezzo al cliente di questa fascia è sotto quello di Simba: va aggiornato prima di proporre il pacchetto.";
      }
      return '';
    });

    watch([problema, numeri], () => {
      emit('change', {
        problema: problema.value,
        dati: problema.value ? null : {
          pickup: pickup.value,
          mesi: numeri.value.mesi,
          valetTotali: numeri.value.valetTotali,
          totaleSimba: numeri.value.totaleSimba,
          totaleVenditore: numeri.value.totaleVenditore,
          bandId: preventivo.value.band.id,
          includedKm: preventivo.value.included_km,
        },
      });
    }, { immediate: true });

    const etichettaSede = (s) => [s.name, [s.address, s.street_number].filter(Boolean).join(' '), s.city]
      .filter(Boolean)
      .join(' · ');
    const fascia = (b) => (b.to_km === null ? `oltre ${b.from_km} km` : `${b.from_km}–${b.to_km} km`);
    const euro = (v) => euroFmt.format(v);

    return {
      uid, sedeId, pickup, mesi, DURATE, stato, preventivo, numeri, problema, calcola, etichettaSede, fascia, euro,
      modoPartenza, altro, casa, casaPronta, testoIndirizzo,
    };
  },
};
</script>

<style scoped>
.pv { display: flex; flex-direction: column; gap: 10px; }
.pv__mesi { width: 200px; }
.pv__route { display: grid; grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); gap: 12px 20px; padding-bottom: 12px; border-bottom: 1px solid var(--color-bg-tertiary); }
.pv__end { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.pv__addr { display: flex; align-items: flex-start; gap: 8px; margin: 0; font-size: 13px; line-height: 1.45; overflow-wrap: anywhere; }
.pv__addr--empty { color: var(--state-attn); font-weight: 600; }
.pv__pin { width: 9px; height: 9px; margin-top: 5px; border-radius: 50%; flex: none; background: var(--color-text-muted); }
.pv__sede { width: 100%; }
.pv__grid--addr { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.pv__grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px 16px; }
.pv__f { display: flex; flex-direction: column; min-width: 0; }
.pv__f--2 { grid-column: span 2; }
.pv__f :deep(.el-input-number) { width: 100%; }
.pv__seg { display: inline-flex; align-self: flex-start; border: 1px solid #dcdfe6; border-radius: 4px; overflow: hidden; }
.pv__seg button {
  font: inherit; font-size: 13px; font-weight: 600; height: 30px; min-width: 40px; padding: 0 12px;
  border: 0; background: #fff; color: var(--color-text-secondary); cursor: pointer;
}
.pv__seg button + button { border-left: 1px solid #dcdfe6; }
.pv__seg button.is-on { background: var(--color-primary); color: #fff; }
.pv__seg button:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 2px; }
.pv__where { margin: 0; font-size: 12px; color: var(--color-text-secondary); min-height: 16px; }
.pv__problem { margin: 0; font-size: 12.5px; font-weight: 600; color: var(--state-attn); }
.pv .fx-stats { margin-top: 0; }
@media (max-width: 700px) {
  .pv__route { grid-template-columns: 1fr; }
  .pv__grid { grid-template-columns: 1fr 1fr; }
}
</style>
